"use client"

// React imports
import { useEffect } from "react"

// Next.js imports
import { useParams } from "next/navigation"

// External SDK imports
import { useGame, useGameState } from "@azuro-org/sdk"
import { GameState, type GameQuery } from "@azuro-org/toolkit"

// Internal hooks and utilities
import { liveStatisticsGameIdStore } from "helpers/stores"
import useChainProviderHook from "@/hooks/useChainProvider"

// Components
import EventInfo, {
  EventInfoSkeleton,
} from "compositions/event/EventInfo/EventInfo"
import Markets, { MarketsSkeleton } from "compositions/event/Markets/Markets"

// Page components
import { OvertimeEventPage } from "@/app/OvertimePages"
import { useIsMounted } from "@/hooks"

type ContentProps = {
  game: NonNullable<GameQuery["game"]>
}
const Content: React.FC<ContentProps> = ({ game }) => {
  const { data: state } = useGameState({
    gameId: game.gameId,
    initialState: game.state,
  })

  useEffect(() => {
    if (game.state === GameState.Live) {
      liveStatisticsGameIdStore.setGameId(game.gameId)
    }
  }, [game.state])

  return (
    <>
      <EventInfo game={game!} state={state} />
      <Markets gameState={state} game={game} />
    </>
  )
}

const AzuroEventPage: React.FC = () => {
  const params = useParams()

  const { data: game, isFetching } = useGame({
    gameId: params!.gameId as string,
  })

  if (isFetching) {
    return (
      <>
        <EventInfoSkeleton />
        <MarketsSkeleton />
      </>
    )
  }

  if (!game) {
    return <div>Game info not found</div>
  }

  return <Content game={game} />
}

export default function EventPage() {
  const { isOvertimeChain } = useChainProviderHook()
  const isMounted = useIsMounted()

  // Show skeleton until component is mounted to prevent hydration mismatch
  if (!isMounted) {
    return (
      <>
        <EventInfoSkeleton />
        <MarketsSkeleton />
      </>
    )
  }

  if (isOvertimeChain) {
    return <OvertimeEventPage />
  }

  return <AzuroEventPage />
}
